import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ChecklistItem {
  id: string;
  org_id: string;
  label: string;
  is_checked: boolean;
  checked_at: string | null;
  checked_by_id: string | null;
  sort_order: number;
  created_at: string;
}

export function useChecklist(orgId: string) {
  return useQuery({
    enabled: !!orgId,
    queryKey: ['checklist', orgId],
    queryFn: async () =>
      (await api.get<ChecklistItem[]>(`/organizations/${orgId}/checklist`)).data,
  });
}

export function useAddChecklistItem(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { label: string }) => {
      const { data } = await api.post<ChecklistItem>(
        `/organizations/${orgId}/checklist`,
        vars,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist', orgId] }),
  });
}

export function useUpdateChecklistItem(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; is_checked: boolean }) => {
      const { data } = await api.patch<ChecklistItem>(
        `/organizations/${orgId}/checklist/${vars.itemId}`,
        { is_checked: vars.is_checked },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist', orgId] }),
  });
}

export function useDeleteChecklistItem(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string }) => {
      await api.delete(`/organizations/${orgId}/checklist/${vars.itemId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist', orgId] }),
  });
}
